import { useEffect, useState } from 'react';
import { faChevronDown, faLink } from '@fortawesome/pro-light-svg-icons';
import { clsx } from 'clsx';

import { ModalProps, PlatformApp, PlatformAppInstance } from '@typings';
import {
  getPlatformAppIntegrationValues,
  getPlatformAppsTemplate,
} from '@services/platformApps.services';
import { contextNamesSelector, platformAppsSelector } from '@selectors';
import { useSelector } from '@hooks';
import {
  cn,
  formatToCamelCase,
  getParamsSchema,
  invariant,
  isArray,
  isFunction,
  noop,
  toastifyResponseError,
} from '@utils';

import { ParamsSchema } from '@components/Schema/SchemaParamsSection/ParamsSchema';
import {
  Button,
  ChaseSpinner,
  Field,
  Icon,
  Modal,
  Table,
} from '@components/Shared';
import { EmptyContent } from '@components/Ui';

import { SchemaAppLogo } from './SchemaAppLogo';

type Props = ModalProps & {
  typeName: string;
  manualMode: boolean;
  value?: PlatformAppInstance.IntegrationValue | null;
  onApply?: (value: PlatformAppInstance.IntegrationValue | null) => void;
};
export const SchemaAppIntegrationsModal: React.FC<Props> = ({
  typeName,
  value: initialValue = null,
  manualMode: initialManualMode = false,
  onApply = noop,
  closeModal = noop,
}) => {
  const { organizationName, clusterName, projectName } =
    useSelector(contextNamesSelector);
  const { templates, instances } = useSelector(platformAppsSelector);

  const [collapsedKey, setCollapsedKey] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState(initialManualMode);
  const [value, setValue] =
    useState<PlatformAppInstance.IntegrationValue | null>(null);
  const [values, setValues] = useState<
    null | PlatformAppInstance.IntegrationValue[]
  >(null);

  useEffect(() => {
    if (initialValue && isArray(values)) {
      const value = values.find(
        ({ instanceId, path }) =>
          instanceId === initialValue.instanceId && path === initialValue.path,
      );

      setValue(value ?? null);
    }
  }, [initialValue, values]);

  useEffect(() => {
    const getIntegrationValues = async () => {
      try {
        setValues(null);

        invariant(organizationName);
        invariant(clusterName);
        invariant(projectName);

        const { items } = await getPlatformAppIntegrationValues({
          organizationName,
          clusterName,
          projectName,
          typeName,
        });

        const values = await Promise.all(
          items.map(async (item) => {
            const { instanceId } = item;

            const instance = instances.find(({ id }) => id === instanceId);

            if (!instance) {
              // eslint-disable-next-line no-console
              console.log('No instance match', item);

              return null;
            }

            const { displayName, templateName, templateVersion } = instance;

            let template = templates.find(
              ({ version, name }) =>
                templateName === name && version === templateVersion,
            );

            if (!template) {
              try {
                template = await getPlatformAppsTemplate({
                  organizationName,
                  clusterName,
                  projectName,
                  name: templateName,
                  version: templateVersion,
                });
              } catch (error) {
                // eslint-disable-next-line no-console
                console.log(error);

                return null;
              }
            }

            if (!template) {
              return null;
            }

            const {
              title,
              shortDescription: description,
              logo,
            } = formatToCamelCase(template) as PlatformApp.Template;

            const value = {
              ...item,
              title: displayName || title,
              templateName,
              templateVersion,
              description,
              logo,
              params: getParamsSchema(template.output),
            };

            return value;
          }),
        );

        setValues(
          values.filter(Boolean) as PlatformAppInstance.IntegrationValue[],
        );
      } catch (error) {
        toastifyResponseError(error);
      }
    };

    getIntegrationValues();
    /**
     * We don't need to track `instance` since it is re-fetched intervally
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeName, organizationName, clusterName, projectName, templates]);

  const handleCollapse = (key: string) => {
    setCollapsedKey((state) => (state === key ? null : key));
  };

  const handleApply = () => {
    if ((value || manualMode) && isFunction(onApply)) {
      onApply(value);
      closeModal();
    }
  };

  const handleManualModeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setManualMode(event.target.checked);
    setValue(null);
  };

  const handleManualModeApply = () => {
    if (isFunction(onApply)) {
      onApply(null);
      closeModal();
    }
  };

  const handleIntegrationValueChange = (
    value: PlatformAppInstance.IntegrationValue,
  ) => {
    setValue(value);
    setManualMode(false);
  };

  const makeIntegrationValue = (
    integrationValue: PlatformAppInstance.IntegrationValue,
  ) => {
    const {
      logo,
      instanceId,
      path,
      title,
      templateName,
      templateVersion,
      params,
      value: instanceValue,
    } = integrationValue;

    const isActive = value?.instanceId === instanceId && value.path === path;
    const key = `${instanceId}-${path}`;
    const isCollapsed = collapsedKey !== key;

    const schema = { [path]: instanceValue };
    const className = cn(
      'cursor-pointer border-neural-02 py-2 transition-colors group-hover:bg-neural-01',
      { '!bg-neural-02': isActive },
    );

    return (
      <Table.Row
        key={key}
        onClick={() => handleIntegrationValueChange(integrationValue)}
        className="group cursor-pointer"
      >
        <Table.Cell className={clsx(className, 'flex-row items-center gap-2')}>
          <Field.Checkbox checked={isActive} />
          <SchemaAppLogo src={logo} size="xs" />
        </Table.Cell>
        <Table.Cell className={className}>{templateName}</Table.Cell>
        <Table.Cell className={className}>{title}</Table.Cell>
        <Table.Cell className={className}>{path}</Table.Cell>
        <Table.Cell className={className}>{templateVersion}</Table.Cell>
        <Table.Cell className={className}>
          <Button
            theme
            stopPropagation
            className="rounded-lg p-2 transition-colors hover:bg-primary-subtle"
            onClick={() => handleCollapse(key)}
          >
            <Icon
              icon={faChevronDown}
              className={clsx('transition-transform', {
                'rotate-180': !isCollapsed,
              })}
            />
          </Button>
        </Table.Cell>
        <Table.Cell
          className={cn(
            className,
            'col-span-full max-w-[960px] shrink flex-row justify-start truncate !bg-white',
            {
              hidden: isCollapsed,
            },
          )}
        >
          <ParamsSchema isTopLevel schema={schema} params={params} />
        </Table.Cell>
      </Table.Row>
    );
  };

  const renderContent = () => {
    if (!values) {
      return (
        <div className="flex h-64 items-center justify-center">
          <ChaseSpinner color="black" className="h-12 w-12" />
        </div>
      );
    }

    if (values.length === 0) {
      return (
        <EmptyContent
          icon={faLink}
          title="Integrations Not Set Up"
          text="It looks like there are no active integrations yet. Set one up to start syncing data"
          className="pb-20 pt-10"
        >
          <Button onClick={handleManualModeApply}>Fill form in manually</Button>
        </EmptyContent>
      );
    }

    return (
      <>
        <div>
          <Table className="mt-4 w-full auto-rows-min grid-cols-[96px_repeat(4,minmax(max-content,auto))_72px] overflow-auto border border-neural-02">
            <Table.Header>
              <Table.Row>
                <Table.Head />
                <Table.Head className="capitalize">Template name</Table.Head>
                <Table.Head className="capitalize">App name</Table.Head>
                <Table.Head>Path</Table.Head>
                <Table.Head>Version</Table.Head>
                <Table.Head />
              </Table.Row>
            </Table.Header>
            <Table.Body>{values.map(makeIntegrationValue)}</Table.Body>
          </Table>
        </div>
        <Modal.Footer className="mt-4 items-center justify-between px-0">
          <Field.Checkbox
            checked={manualMode}
            onChange={handleManualModeChange}
          >
            Choose manual mode instead
          </Field.Checkbox>
          <div className="flex gap-4">
            <Button variant="secondary" onClick={closeModal}>
              Close
            </Button>
            <Button
              disabled={!value && !manualMode}
              className="px-10"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </Modal.Footer>
      </>
    );
  };

  return (
    <Modal.Content title="App Integrations" className="w-[1024px]">
      {renderContent()}
    </Modal.Content>
  );
};
